enum EditorType {
  INVALID_EDITOR
  USER
  CLIENT
  SERVICE
}

enum MortgageReason {
  REASON_FTB
  REASON_HOUSE_MOVE
  REASON_REMORTGAGE
  REASON_BTL
  REASON_BTL_REMORTGAGE
  REASON_EQUITY_RELEASE
  REASON_FURTHER_ADVANCE
  REASON_BUSINESS_PROTECTION
  REASON_PROTECTION
  INVALID_MORTGAGE_REASON
  REASON_COMMERCIAL
  REASON_BRIDGING
  REASON_GENERAL_INSURANCE
}

enum CancellationReason {
  INVALID_CANCELLATION_REASON
  NO_RESPONSE
  CLIENT_DECLINED_PRODUCT
  NO_PRODUCT_AVAILABLE
  DUPLICATE_CASE
  INCORRECT_CONTACT_DETAILS
  CLIENT_NO_LONGER_REQUIRES_PRODUCT
  CLIENT_OBTAINED_PRODUCT_ELSEWHERE
  FEE_CONCERNS
  LENDER_DECLINED_APPLICATION
  ADVERSE_CREDIT
  PROPERTY_NOT_FOUND
  OTHER
}

enum ContactType {
  INVALID
  REFERRER
  ADMINISTRATOR
  LEAD_QUALIFIER
  CASE_MANAGER
  WATCHER
  REVIEWER
  INTRODUCER
  ADVISOR
  ESTATE_AGENT
  VALUER
  CONVEYANCER
}

enum RelationshipType {
  INVALID
  REFERRER
  ADMINISTRATOR
  LEAD_QUALIFIER
  CASE_MANAGER
  WATCHER
  REVIEWER
}

enum CaseStatus {
  INVALID_STATUS
  PRE_RECOMMENDATION
  REVIEW
  PRE_APPLICATION
  POST_APPLICATION
  NOT_PROCEEDING
  IMPORTED_COMPLETE
  IMPORTING
  POST_RECOMMENDATION_REVIEW
  LEAD
  APPLICATION_SUBMITTED
  AWAITING_VALUATION
  AWAITING_OFFER
  OFFER_RECEIVED
  EXCHANGE
  COMPLETE
  REFERRED
}

enum UpdateCaseStatusRequestUpdateType {
  INVALID_UPDATE_TYPE
  PRESALE_CHECK
  RECOMMENDATION
  ARCHIVE_RECOMMENDATION
  COMPLETE
  NOT_PROCEEDING
  REVIEW_REQUIRED
  REVIEW_COMPLETE
  REOPEN
  PRE_RECOMMENDATION
  APPLICATION_SUBMITTED
  AWAITING_VALUATION
  AWAITING_OFFER
  OFFERED
  EXCHANGE
  LEAD
  REFERRED
}

# this is the same name as that in the rest api documnet
enum CaseDetailsFlagFlagType {
  INVALID_FLAG_TYPE
  FLAG_AML
  FLAG_DEBT_CONSOLIDATION
  FLAG_EQUITY_RELEASE
  FLAG_FINANCIAL_CRIME
  FLAG_GIFTED_DEPOSIT
  FLAG_HIGH_COMMISSION
  FLAG_HIGH_RISK_DEBT_CONSOLIDATION
  FLAG_LENDING_INTO_RETIREMENT
  FLAG_NATIONALITY
  FLAG_PEP
  FLAG_PROVISIONAL_COMPETENCY
  FLAG_REDEMPTION_PENALTY
  FLAG_RESIDENTIAL_INTEREST_ONLY
  FLAG_RIGHT_TO_BUY
  FLAG_SECOND_JOB
  FLAG_VULNERABLE
  FLAG_VULNERABLE_CLIENT
  FLAG_PROTECTION_PROVISIONAL_COMPETENCY
  FLAG_MANUAL_REVIEW
  FLAG_THIRD_PARTY_REVIEW
  FLAG_AUTOMATED_SAMPLE
  FLAG_COMPLAINT
}

enum CaseDetailsMortgageClassType {
  INVALID_MORTGAGE_CLASS
  CLASS_FIXED
  CLASS_DISCOUNT
  CLASS_VARIABLE
  CLASS_CAPPED
  CLASS_LIBOR
  CLASS_TRACKER
  CLASS_STEPPED
}

enum RepaymentVehicleType {
  ENDOWMENT_ISA
  INVESTMENTS
  PENSION
  SALE_OF_BUY_TO_LET_PROPERTY
  SALE_OF_SECURITY_PROPERTY
  SAVINGS
}

enum ValuationType {
  STANDARD_VALUATION
  HOMEBUYERS_REPORT
  FULL_BUILDING_SURVEY
  DRIVE_BY_VALUATION
  AUTOMATED_VALUATION_MODEL
  DESKTOP_VALUATION
  HOME_REPORT
  NO_VALUATION
}

enum CaseRelationshipType {
  INVALID_RELATIONSHIP_TYPE
  REFERRAL_TARGET
  REFERRAL_SOURCE
  REMORTGAGE_OF
  REMORTGAGE_LEAD
  SHARED_PROPERTY_RECORD
}

enum RequirementType {
  INCOME_PROTECTION
  LIFE_AND_CRITICAL_ILLNESS
  FAMILY_INCOME_BENEFITS
  WHOLE_OF_LIFE
  PRIVATE_MEDICAL_INSURANCE
  RELEVANT_LIFE
  EXECUTIVE_IP
}

enum ClientPortalChangesEditorType {
  INVALID_EDITOR
  USER
  CLIENT
  SERVICE
}

input CaseFeeInput {
  fee_type: FeeType!
  value: String
}

input UpdateCaseFeeInput {
  fees: [CaseFeeInput!]!
  fee_option_title: String
}

input CaseRequirementsInput {
  protection: ProtectionInput
  selectedID: String
  requirement: RequirementInput
  requirement_type: RequirementType
}

input CaseDetailsFlagInput {
  flag_id: String
  flag_type: String
  created_at: String
  reviews: [CaseDetailsFlagReviewInput!]
}

input CaseDetailsFlagReviewInput {
  review_passed: Boolean
  created_at: String
  reviewer_user_id: String
  reviewer_private_notes: String
  reviewer_notes: String
  review_grade: FlagReviewGrade
  reviewed_case_version: Int
  skip_future_reviews: Boolean
}

type CalculatedCaseValues {
  current_mortgage_comparison_cost: String
  lender_proposed_comparison_cost: String
  selected_mortgage_comparison_cost: String
  total_required_loan_amount: String
  total_additional_borrowing: String
  current_totals: CaseCurrentTotals
}

type CaseCurrentTotals {
  total_outstanding_balances: String
}

type OverrideEidvResult {
  client_id: String!
  override_result: Boolean!
}

type CasePreferences {
  interest_only_repayment_strategy: String
  upper_limit: String
  fixed_rate: Boolean
  early_year_discount: Boolean
  no_early_repayment_charge: Boolean
  no_tie_in: Boolean
  fee_to_loan: Boolean
  vary_repayment_amount: Boolean
  payment_holiday: Boolean
  link_finances: Boolean
  preference_details: String
  preference_target_property: String
  preference_target_deposit: String
  preference_term: Int
  preference_repayment_percentage: Int
  preference_related_property_sale: String
  preference_cashback: Boolean
  preference_initial_period_months: Int
  preference_mortgage_class: CaseDetailsMortgageClassType
  preference_free_conveyancing: Boolean
  preference_free_valuations: Boolean
  preference_loan_amount: String
  preference_completion_speed_important: Boolean
  preference_tracker: Boolean
  preference_overpayment: Boolean
  preference_target_property_value: String
  preference_mortgage_loan_amount: String
  additional_loan_required: Boolean
  additional_loans: [AdditionalLoans]
  preference_reason_for_debt_consolidation: ReasonForDebtConsolidation
  preference_repayment_vehicle_type: RepaymentVehicleType
}

type RelatedUser {
  id: String!
  user_id: String!
  relationship_type: ContactType!
  commission_share_basis_points: Int!
}

type RelatedCase {
  id: String!
  case_id: String!
  relationship_type: CaseRelationshipType
  preference_mortgage_reason: MortgageReason
  firm: String
  advisor: String
  status: CaseStatus
  visible: Boolean
  address: LutherAddress
  property_id: String
  firm_id: String
  representative_of: String
}

type ThirdPartyOrg {
  type: String
  firm_name: String
  name: String
  work_phone: String
  id: ID
  email_address: String
  address: LutherAddress
}

type LeadMetadata {
  source: String
  campaign: String
  content: String
}

type ExternalCaseSource {
  id: String
  name: ExternalIdNames
}

type ExternalCase {
  id: String
  source: ExternalCaseSource
}

enum CaseDocumentRequirementSource {
  INVALID_SOURCE
  USER_VIA_MESSAGE
  USER
}

type CaseDocumentRequirement {
  requirement_id: ID
  source: CaseDocumentRequirementSource
  source_id: ID
  created_at: String
  details: DocumentRequirementDetails
}

type Case {
  id: ID!
  clients(fromClientPortal: Boolean, withFullDetails: Boolean): [ClientVersion!]!
  clientsUser(showBasicInfo: Boolean): [ClientVersion!]
  client_ids: [String!]
  owner_user_id: ID
  owner: User
  owner_organisation_id: String
  owner_organisation(fromClientPortal: Boolean): Organisation
  preference_mortgage_reason: MortgageReason
  status: CaseStatus
  details: String
  mortgages: [Mortgage!]
  mortgages_cp: [Mortgage!]
  protection_products: [ProtectionProduct!]
  fees: [NewFee!]
  fee_option_title: String
  predicted_revenue: BigInt
  new_property: PropertyVersion
  new_property_cp: PropertyVersion
  remortgage_property: PropertyVersion
  remortgage_property_cp: PropertyVersion
  property_portfolio(includeDirectorsProperties: Boolean): [PropertyVersion!]
  verifications(show_pass: Boolean, show_documents: Boolean): CaseVerification
  flags: [CaseDetailsFlag!]
  reason_for_further_advance: String
  recommendation_term_reason: String
  recommendation_reason_for_affordability: String
  recommendation_reason_for_recommendation: String
  reason_for_recommendation_product_type: String
  reason_for_recommendation_initial_term: String
  recommendation_reason_for_remortgage_cost_increase: String
  recommendation_reason_for_recommendation_term_increase: String
  recommendation_reason_term_into_retirement: String
  recommendation_reason_additional_borrowing: String
  debt_consolidation_alternative_options: String
  debt_consolidation_reason_for_consolidated_debts: String
  suitability_report_recommendation_introduction: String
  recommendation_reason_strategy_for_btl_void_period_coverage: String
  recommendation_reason_help_to_buy_loan_repayment_strategy: String
  recommendation_reason_retirement_interest_only: String
  pay_rise_expected_details: String
  recommendation_reason_for_further_advance: String
  recommendation_reason_entitlement_to_state_benefits: String
  lender_has_proposed_new_product: Boolean
  regulated_by: String
  has_previously_owned_property: Boolean
  agreed_monthly_protection_budget: String
  has_seen_face_to_face: Boolean
  impersonation_check_performed: Boolean
  impersonation_check_details: String
  is_debt_consolidation: Boolean
  btl_has_lived: Boolean
  btl_will_lived: Boolean
  btl_is_operating_a_business: Boolean
  btl_through_company: Boolean
  protection: Protection
  is_introduced: Boolean
  introducer_id: String
  calculated_values: CalculatedCaseValues
  introducer_details: String
  shared_equity_repayment_details: String
  recommendation_strategy_for_payment_erc: String
  recommendation_reason_for_equity_disparity: String
  override_client_eidv_results: [OverrideEidvResult!]
  third_party_orgs: [ThirdPartyOrg!]
  related_users: [RelatedUser!]
  related_cases: [RelatedCase!]
  last_update: String
  last_significant_update: String

  # Preferences
  preference_interest_only_repayment_strategy: String
  preference_upper_limit: String
  preference_fixed_rate: Boolean
  preference_early_year_discount: Boolean
  preference_no_early_repayment_charge: Boolean
  preference_no_tie_in: Boolean
  preference_fee_to_loan: Boolean
  preference_vary_repayment_amount: Boolean
  preference_payment_holiday: Boolean
  preference_link_finances: Boolean
  preference_details: String
  preference_term: Int
  preference_repayment_percentage: Int
  change_of_preference_reason: [String!]
  preference_cashback: Boolean
  preference_initial_period_months: Int
  preference_mortgage_class: CaseDetailsMortgageClassType
  preference_free_conveyancing: Boolean
  preference_free_valuations: Boolean
  preference_completion_speed_important: Boolean
  preference_tracker: Boolean
  preference_overpayment: Boolean
  preference_target_property_value: String
  preference_loan_amount: String
  preference_target_deposit: String
  preference_target_property: String
  preference_related_property_sale: String
  shared_budget: Boolean
  preference_mortgage_loan_amount: String
  additional_loans: [AdditionalLoans!]
  additional_loan_required: Boolean
  preference_reason_for_debt_consolidation: ReasonForDebtConsolidation
  cancellation_reason: CancellationReason
  transition_client_versions: [TransitionClientVersion!]
  transition_mortgage_versions: [TransitionMortgageVersion!]
  transition_property_versions: [TransitionPropertyVersion!]
  transition_protection_versions: [TransitionProtectionVersion!]
  conveyancing_status: ConveyancingStatus
  conveyancing_updates: [ConveyancingUpdate!]
  own_solicitor_details: OwnSolicitor
  conveyancing_type: ConveyancingType
  referrals: [Referral!]
  createLeadCaseReferral: LeadCaseReferral

  # Documents
  documents(show_documents: Boolean): [DocumentEntity!]
  miDocuments(filters: MIDocumentSummaryFiltersInput, fromClientPortal: Boolean): MIDocumentSummaryResponse
  document_categories(id: ID!, filters: MIDocumentTotalsFiltersInput): [MIDocumentCategory!]
  document_totals(filters: MIDocumentTotalsFiltersInput): [MIDocumentTotal!]

  # External data
  external: [ExternalCase]

  # IO data
  lead_metadata: LeadMetadata

  # Additional Metadata
  additional_metadata: [AdditionalMetadata]
  survey_answers: [CaseSurveyAnswer]

  dip_result: [CaseLevelDipResult!]

  addendum_reasons: AddendumReasons
  document_requirements: [CaseDocumentRequirement!]
}

type LeadCaseReferral {
  case: LeadCaseReferralResponse
}

type LeadCaseReferralResponse {
  case_id: String
}

type CaseLevelDipResult {
  acre_dip_id: String!
  client_ids: [String!]
  provider_dip_reference: String
  provider_application_reference: String
  provider_case_id: String
  amount: String
  dip_status: CaseDetailsCaseLevelDipResultDipStatusType!
  dip_details: DipDetails
  lender: String!
}

type ClientPortalChanges {
  cp_changes: ClientPortalChangesEntities
}

type ClientPortalChangesDocument {
  entity_id: String
  editor_id: String
  editor_type: ClientPortalChangesEditorType
  type: DocumentKind
  document_name: String
  date_stored: String
}

type ClientPortalChangesEntities {
  clients: [ClientPortalChangesEntitiesFields!]
  mortgages: [ClientPortalChangesEntitiesFields!]
  properties: [ClientPortalChangesEntitiesFields!]
  documents: [ClientPortalChangesDocument!]
}

type ClientPortalChangesEntitiesFields {
  entity_id: String
  editor_id: String
  editor_type: ClientPortalChangesEditorType
  field: String
  version_from: String
  version_to: String
  change_type: String
  current: JSON
  differences: JSON
  previous: JSON
}

input CaseLevelDipResultInput {
  acre_dip_id: String!
  client_ids: [String!]
  provider_dip_reference: String
  provider_application_reference: String
  provider_case_id: String
  amount: String
  dip_status: CaseDetailsCaseLevelDipResultDipStatusType!
  dip_details: DipDetailsInput
  lender: String!
}

enum CaseDetailsCaseLevelDipResultDipStatusType {
  INVALID_DIP_STATUS
  PENDING
  DECLINED
  ACCEPTED
  REFER
  ERROR
  REDIRECT
}

type CaseSurveyAnswer {
  client_id: String! # client_id points to the uuidv4 of the client entity.
  answers: [Answer!]! # answers contains a list of client_id answers for the survey.
  completed_at: String
  modified_at: String
}

type Answer {
  question_id: String! # question_id points to the question that this answer is for.
  question_version: Int! # question_version points to the question version that this answer is for.
  five_scale_answer: Int # five_scale_answer holds the uuidv4 of a five scale answer entity.
  ten_scale_answer: Int # ten_scale_answer holds the uuidv4 of a ten scale answer entity.
  zero_ten_scale_answer: Int # ten_scale_answer holds the answer of the question
  free_text_answer: String # free_text_answer holds the actual text of the answer.
  bool_answer: Boolean # bool_answer holds either true or false of the answer.
  single_choice_answer: String # single_choice_answer holds the single choice text.
  multiple_choice_answer: [String] # multiple_choice_answer holds the text of multiple choices.
}

type TransitionClientVersion {
  entity_versions: [ClientEntityVersion!]
  transition_to: CaseStatus!
  created_at: String!
}

type TransitionMortgageVersion {
  entity_versions: [MortgageEntityVersion!]
  transition_to: CaseStatus!
  created_at: String!
}

type TransitionPropertyVersion {
  entity_versions: [PropertyEntityVersion!]
  transition_to: CaseStatus!
  created_at: String!
}

type TransitionProtectionVersion {
  entity_versions: [ProtectionEntityVersion!]
  transition_to: CaseStatus!
  created_at: String!
}

type VersionWithChangedFields {
  version: Int
  editor: Editor
  changed_fields: [String!]
}

type CaseVersion implements VersionedObject {
  clients: [ClientVersion!]
  id: ID!
  created_at: String!
  version: Int
  version_created_at: String
  details: Case!
  versions: [VersionWithChangedFields!]
}

type Referral {
  referral_type: ReferralType
  partner: ReferralPartnerType
  third_party_id: String
  status: ReferralStatus
  contact_history: [ReferralContactHistory!]
  referral_source: ReferralSource
  referral_source_id: String
}

type ReferralContactHistory {
  contact_type: ReferralContactType
  identifier: String
  created_at: String
  clicked_at: String
  clicks: Int
  third_party_id: String
  opened_at: String
  delivered: Boolean
}

type AdditionalMetadata {
  name: String
  string_value: String
}

enum ReferralContactType {
  INVALID_CONTACT_TYPE
  EMAIL
  OTHER
}

input LeadMetadataInput {
  source: String
  campaign: String
  content: String
}

input AdditionalMetadataInput {
  name: String
  string_value: String
}

input ExternalCaseSourceInput {
  id: String
  name: ExternalIdNames
}

input ExternalCaseInput {
  id: String
  source: ExternalCaseSourceInput
}

input CaseInput {
  owner_user_id: ID
  owner_organisation_id: ID
  client_ids: [String!]
  mortgage_ids: [String]
  status: CaseStatus
  fees: [CaseFeeInput]
  fee_option_title: String
  flags: [CaseDetailsFlagInput!]
  change_of_preference_reason: [String!]
  preference_mortgage_reason: MortgageReason
  preference_details: String
  preference_interest_only_repayment_strategy: String
  preference_upper_limit: String
  preference_fixed_rate: Boolean
  preference_early_year_discount: Boolean
  preference_no_early_repayment_charge: Boolean
  preference_no_tie_in: Boolean
  preference_completion_speed_important: Boolean
  preference_fee_to_loan: Boolean
  preference_vary_repayment_amount: Boolean
  preference_payment_holiday: Boolean
  preference_link_finances: Boolean
  preference_target_property: String
  preference_target_deposit: String
  preference_target_property_value: String
  preference_loan_amount: String
  preference_term: Int
  preference_repayment_percentage: Int
  preference_related_property_sale: String
  reason_for_further_advance: String
  recommendation_term_reason: String
  recommendation_reason_for_affordability: String
  recommendation_reason_for_recommendation: String
  recommendation_reason_for_remortgage_cost_increase: String
  recommendation_reason_for_recommendation_term_increase: String
  recommendation_reason_term_into_retirement: String
  debt_consolidation_alternative_options: String
  debt_consolidation_reason_for_consolidated_debts: String
  recommendation_reason_additional_borrowing: String
  suitability_report_recommendation_introduction: String
  recommendation_reason_strategy_for_btl_void_period_coverage: String
  recommendation_reason_help_to_buy_loan_repayment_strategy: String
  recommendation_reason_retirement_interest_only: String
  recommendation_reason_for_further_advance: String
  recommendation_reason_entitlement_to_state_benefits: String
  lender_has_proposed_new_product: Boolean
  preference_cashback: Boolean
  preference_overpayment: Boolean
  preference_initial_period_months: Int
  preference_mortgage_class: CaseDetailsMortgageClassType
  preference_free_conveyancing: Boolean
  preference_free_valuations: Boolean
  has_previously_owned_property: Boolean
  has_seen_face_to_face: Boolean
  is_debt_consolidation: Boolean
  impersonation_check_performed: Boolean
  impersonation_check_details: String
  btl_has_lived: Boolean
  btl_will_lived: Boolean
  btl_is_operating_a_business: Boolean
  btl_through_company: Boolean
  preference_tracker: Boolean
  protection: ProtectionInput
  is_introduced: Boolean
  introducer_details: String
  introducer_id: String
  shared_budget: Boolean
  preference_mortgage_loan_amount: String
  additional_loans: [AdditionalLoansInput]
  additional_loan_required: Boolean
  preference_reason_for_debt_consolidation: ReasonForDebtConsolidation
  cancellation_reason: CancellationReason
  conveyancing_type: ConveyancingType
  own_solicitor_details: OwnSolicitorInput
  conveyancing_status: ConveyancingStatus
  override_client_eidv_results: [OverrideEidvResultInput!]
  third_party_orgs: [ThirdPartyOrgInput]
  related_users: [RelatedUserInput]
  referrals: [ReferralInput]
  lead_metadata: LeadMetadataInput
  additional_metadata: [AdditionalMetadataInput]
  survey_answers: [SurveyAnswerInput]
  external: [ExternalCaseInput]
  dip_result: [CaseLevelDipResultInput!]
  addendum_reasons: AddendumReasonsInput
  document_requirements: [CaseDocumentRequirementInput!]
}

input CaseDocumentRequirementInput {
  requirement_id: ID
  source: CaseDocumentRequirementSource
  source_id: ID
  details: DocumentRequirementDetailsInput
}

input SurveyAnswerInput {
  client_id: String! # client_id points to the uuidv4 of the client entity.
  answers: [AnswerInput!]! # answers contains a list of client_id answers for the survey.
}

input AnswerInput {
  question_id: String! # question_id points to the question that this answer is for.
  question_version: Int! # question_version points to the question version that this answer is for.
  five_scale_answer: Int # five_scale_answer holds the uuidv4 of a five scale answer entity.
  ten_scale_answer: Int # ten_scale_answer holds the uuidv4 of a ten scale answer entity.
  zero_ten_scale_answer: Int # ten_scale_answer holds the answer of the question
  free_text_answer: String # free_text_answer holds the actual text of the answer.
  bool_answer: Boolean # bool_answer holds either true or false of the answer.
  single_choice_answer: String # single_choice_answer holds the single choice text.
  multiple_choice_answer: [String] # multiple_choice_answer holds the text of multiple choices.
}

input ThirdPartyOrgInput {
  type: String
  firm_name: String
  name: String
  work_phone: String
  id: ID
  email_address: String
  address: [String!]
}

input RelatedUserInput {
  user_id: String!
  relationship_type: ContactType!
  commission_share_basis_points: Int!
}

input ReferralInput {
  partner: ReferralPartnerType
  referral_type: ReferralType
  status: ReferralStatus
  third_party_id: String
  contact_history: [ReferralContactHistoryInput]
  referral_source: ReferralSource
  referral_source_id: String
}

input ReferralContactHistoryInput {
  contact_type: ReferralContactType
  identifier: String
  created_at: String
  clicked_at: String
  clicks: Int
  third_party_id: String
  opened_at: String
  delivered: Boolean
}

input OverrideEidvResultInput {
  client_id: String!
  override_result: Boolean!
}

enum ConveyancingType {
  NO_SELECTION
  LENDER_PROVIDED
  ACRE_REFERRAL
  OWN_SOLICITOR
  NOT_NEEDED
}

enum ConveyancingPartner {
  INVALID_CONVEYANCING_PARTNER
  SIMPLIFY_MHM
}

enum ConveyancingStatus {
  INVALID_CONVEYANCING_STATUS
  QUOTED
  ACCEPTED
  DECLINED
  AWAITING_CLIENT
  AWAITING_SOLICITOR
  AWAITING_SEARCHES
  EXCHANGE
  COMPLETED
}

input OwnSolicitorInput {
  firm_name: String
  contact_name: String
  email_address: String
  phone_number: String
  dx_number: String
  dx_town: String
  case_reference: String
}

type OwnSolicitor {
  firm_name: String
  contact_name: String
  email_address: String
  phone_number: String
  dx_number: String
  dx_town: String
  case_reference: String
}

input CasePreferencesInput {
  preference_interest_only_repayment_strategy: String
  preference_upper_limit: String
  preference_fixed_rate: Boolean
  preference_early_year_discount: Boolean
  preference_no_early_repayment_charge: Boolean
  preference_no_tie_in: Boolean
  preference_fee_to_loan: Boolean
  preference_vary_repayment_amount: Boolean
  preference_payment_holiday: Boolean
  preference_link_finances: Boolean
  preference_details: String
  preference_term: Int
  preference_repayment_percentage: Int
  change_of_preference_reason: [String!]
  preference_cashback: Boolean
  preference_initial_period_months: Int
  preference_mortgage_class: CaseDetailsMortgageClassType
  preference_free_conveyancing: Boolean
  preference_free_valuations: Boolean
  preference_completion_speed_important: Boolean
  preference_tracker: Boolean
  preference_overpayment: Boolean
  preference_target_property_value: String
  preference_loan_amount: String
  preference_mortgage_loan_amount: String
  additional_loan_required: Boolean
  additional_loans: [AdditionalLoansInput]
  preference_repayment_vehicle_type: RepaymentVehicleType
}

input CaseSuitabilityInput {
  preference_details: String
  recommendation_term_reason: String
  recommendation_reason_for_affordability: String
  recommendation_reason_for_recommendation: String
  recommendation_reason_for_remortgage_cost_increase: String
  recommendation_reason_for_recommendation_term_increase: String
  recommendation_reason_term_into_retirement: String
  recommendation_reason_additional_borrowing: String
  reason_for_recommendation_initial_term: String
  reason_for_recommendation_product_type: String
  debt_consolidation_alternative_options: String
  debt_consolidation_reason_for_consolidated_debts: String
  suitability_report_recommendation_introduction: String
  recommendation_reason_strategy_for_btl_void_period_coverage: String
  recommendation_reason_help_to_buy_loan_repayment_strategy: String
  recommendation_reason_retirement_interest_only: String
  pay_rise_expected_details: String
  recommendation_reason_for_further_advance: String
  recommendation_reason_entitlement_to_state_benefits: String
  change_of_preference_reason: [String!]
  mortgages: [MortgageReasonInputForCase]
  preference_interest_only_repayment_strategy: String
  recommendation_reason_for_equity_disparity: String
  shared_equity_repayment_details: String
  recommendation_strategy_for_payment_erc: String
  protection: ProtectionInput
  addendum_reasons: AddendumReasonsInput
}

type GetCaseRequest {
  case_details: Boolean
  case_ids: [ID!]
  filter_client_ids: [String]
  filter_owner_user_id: String
  filter_owner_org_id: String
  filter_regulated_by_org_id: String
  filter_status: [CaseStatus!]
  bookmark: String
  page_size: Int
}

input GetCaseRequestArgs {
  params: GetCaseRequestInput
}

input GetCaseRequestInput {
  case_details: Boolean
  case_ids: [ID!]
  filter_client_ids: [String]
  filter_owner_user_id: String
  filter_owner_org_id: String
  filter_regulated_by_org_id: String
  filter_status: [CaseStatus!]
  filter_order_reverse: Boolean
  bookmark: String
  page_size: Int
}

type CaseResponse {
  cases: [CaseVersion!]
  next_page: GetCaseRequest
}

type CaseVerification {
  verification_collections: [VerificationCollection!]!
  will_need_review: Boolean
  tested_client_ids: [String!]
  tested_case_ids: [String!]
  tested_mortgage_ids: [String!]
  tested_property_ids: [String!]
}

type CaseDetailsVersionSummary {
  status: CaseStatus
}

type CaseDetailsFlag {
  flag_id: String!
  flag_type: CaseDetailsFlagFlagType!
  created_at: String
  reviews: [CaseDetailsFlagReview!]
}

enum FlagReviewGrade {
  GRADE_A
  GRADE_B
  GRADE_C
  GRADE_D
  INVALID_REVIEW_GRADE
}

type CaseDetailsFlagReview {
  review_passed: Boolean
  created_at: String
  reviewer_user_id: String
  reviewer_private_notes: String
  reviewer_notes: String
  review_grade: FlagReviewGrade
  reviewed_case_version: Int
  skip_future_reviews: Boolean
}

input CaseFlagReviewInput {
  case_id: ID!
  flag_id: ID!
  reviewed_case_version: Int
  review_passed: Boolean
  reviewer_notes: String
  review_grade: FlagReviewGrade
  reviewer_private_notes: String
  skip_future_reviews: Boolean
}

input CaseUpdateStatusInput {
  case_id: ID
  update: UpdateCaseStatusRequestUpdateType
}

type CaseUpdateStatusResponse {
  verification_collections: [VerificationCollection!]
  documents: [DocumentEntity!]
  case: CaseVersion
}

type UpdateCaseAndAddNoteResponse {
  case: CaseVersion
  note: Note
}

type CaseCompliance {
  cases: [CaseVersion!]
  next_page: GetCaseRequest
  organisation_name: String
}

type AdditionalLoans {
  id: String
  loan_amount: String
  loan_purpose: LoanType
}

input AdditionalLoansInput {
  id: String
  loan_amount: String
  loan_purpose: LoanType
}

enum ReasonForDebtConsolidation {
  AFFORDABLE_ADDITIONAL_BORROWING
  REDUCE_COST_OF_CREDIT
  IMPROVE_AFFORDABILITY
}

enum LoanType {
  INVALID_LOAN_PURPOSE
  HOME_IMPROVEMENTS
  BUY_OTHER_PROPERTY
  DIVORCE_SETTLEMENT
  CAPITAL_RAISING
  DEBT_CONSOLIDATION
  CAR
  HOLIDAY
  SCHOOL_FEES
  BUSINESS_PURPOSES
  STAIRCASING
  MOVING_COSTS
  STAMP_DUTY
  OTHER
}

enum LengthOfTimePaymentWithoutWork {
  LESS_ONE_MONTH
  ONE_TO_THREE_MONTHS
  THREE_TO_SIX_MONTHS
  SIX_TO_TWELVE_MONTHS
  ONE_YEAR_PLUS
  UNSURE
}

enum Scenario {
  INVALID_SCENARIO
  DEATH
  CRITICAL_ILLNESS
  DEATH_OR_EARLIER_CRITICAL_ILLNESS
}

enum CoverFrequency {
  INVALID
  UNDEFINED
  WEEKLY
  MONTHLY
  QUARTERLY
  ANNUALLY
  ONCE
}

type ProtectionObjectives {
  advice_limit: Boolean
  advice_limit_details: String
  protection_objectives_circumstances: String
}

type LifeAndCriticalIllness {
  id: ID
  client_ids: [String]
  scenario: Scenario
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
  cover_amount_depreciation_rate: Int
}

type FamilyIncomeBenefits {
  id: ID
  client_ids: [String]
  scenario: Scenario
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
}

type IncomeProtection {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  deferral_weeks: Int
  cover_amount: String
  cover_amount_type: CoverAmountType
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
}

type WholeOfLife {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
  benefit_type: String
}

type PrivateMedicalInsurance {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  cover_reason: String
  benefit_type: String
}

type RelevantLife {
  id: ID
  client_ids: [String]
  scenario: Scenario
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
  cover_amount_depreciation_rate: Int
}

type ExecutiveIP {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  deferral_weeks: Int
  cover_amount: String
  cover_amount_type: CoverAmountType
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
}

type ProtectionRequirements {
  objectives: ProtectionObjectives
  life_and_critical_illness: [LifeAndCriticalIllness!]
  family_income_benefits: [FamilyIncomeBenefits!]
  income_protection: [IncomeProtection!]
  whole_of_life: [WholeOfLife!]
  private_medical_insurance: [PrivateMedicalInsurance!]
  relevant_life: [RelevantLife!]
  executive_ip: [ExecutiveIP!]
}

type Protection {
  has_client_accepted_protection_advice: Boolean
  has_client_accepted_protection_needs: Boolean
  client_declined_protection_needs_reason: String
  client_advice_limit_details: String
  death_needs_income_amount: String
  death_needs_income_term: Int
  death_needs_lump_sum_amount: String
  has_existing_policies: Boolean
  death_needs_lump_sum_term: Int
  illness_needs_income_amount: String
  illness_needs_income_term: Int
  death_needs_lump_sum_term_justification: String
  death_needs_income_term_justification: String
  illness_needs_deferrment_period_weeks: Int
  illness_needs_term_justification: String
  illness_needs_met_by_preexisting: Boolean
  death_lump_sum_needs_met_by_preexisting: Boolean
  death_income_needs_met_by_preexisting: Boolean
  any_remaning_shortfalls: Boolean
  justify_remaining_shortfalls: String
  additional_notes: String
  length_of_time_payment_without_work: LengthOfTimePaymentWithoutWork
  include_employee_benefits_in_shortfall: Boolean
  has_employee_benefits: Boolean
  employee_benefits_shortfall_reasoning: String
  client_protection_objectives_circumstances: String
  insistent_client_indicator: Boolean
  inheritance_advice_indicator: Boolean
  income_has_critical_illness_requirement: Boolean
  income_has_death_requirement: Boolean
  lump_sum_has_critical_illness_requirement: Boolean
  lump_sum_has_death_requirement: Boolean
  death_needs_lump_sum_client_ids: [String]
  death_needs_income_client_ids: [String]
  illness_needs_client_ids: [String]
  funeral_needs_client_ids: [String]
  funeral_needs_lump_sum_amount: String
  funeral_needs_justification: String
  funeral_needs_met_by_preexisting: Boolean
  recommend_placing_in_trust: Boolean
  specific_trust_recommendation: Boolean
  trust_details: String
  client_confirms_terms_exceeding_retirement_affordable: Boolean
  client_advice_limit: Boolean
  suitability_report_recommendation_introduction_protection: String
  additional_considerations_detail: String
  rebroke_decision_advantages_and_disadvantages: String
  additional_budget_details: String
  death_needs_lump_sum_amount_justification: String
  death_needs_income_amount_justification: String
  illness_needs_income_amount_justification: String
  requirements: ProtectionRequirements
}

input ProtectionObjectivesInput {
  advice_limit: Boolean
  advice_limit_details: String
  protection_objectives_circumstances: String
}

input RequirementInput {
  id: ID
  client_ids: [String]
  scenario: Scenario
  deferral_weeks: Int
  existing_policy: Boolean
  cover_amount: String
  cover_amount_type: CoverAmountType
  cover_amount_depreciation_rate: Int
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
}

input LifeAndCriticalIllnessInput {
  id: ID
  client_ids: [String]
  scenario: Scenario
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
  cover_amount_depreciation_rate: Int
}

input FamilyIncomeBenefitsInput {
  id: ID
  client_ids: [String]
  scenario: Scenario
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
}

input IncomeProtectionInput {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  deferral_weeks: Int
  cover_amount: String
  cover_amount_type: CoverAmountType
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
}

input WholeOfLifeInput {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
}

input PrivateMedicalInsuranceInput {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  cover_reason: String
}

input RelevantLifeInput {
  id: ID
  client_ids: [String]
  scenario: Scenario
  existing_policy: Boolean
  cover_amount: String
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
  benefit_type: String
  cover_amount_depreciation_rate: Int
}

input ExecutiveIPInput {
  id: ID
  client_ids: [String]
  existing_policy: Boolean
  deferral_weeks: Int
  cover_amount: String
  cover_amount_type: CoverAmountType
  cover_frequency: CoverFrequency
  cover_reason: String
  term_years: Int
  term_age: Int
  term_reason: String
}

input ProtectionRequirementsInput {
  objectives: ProtectionObjectivesInput
  life_and_critical_illness: [LifeAndCriticalIllnessInput!]
  family_income_benefits: [FamilyIncomeBenefitsInput!]
  income_protection: [IncomeProtectionInput!]
  whole_of_life: [WholeOfLifeInput!]
  private_medical_insurance: [PrivateMedicalInsuranceInput!]
  relevant_life: [RelevantLifeInput!]
  executive_ip: [ExecutiveIPInput!]
}

input ProtectionInput {
  has_client_accepted_protection_advice: Boolean
  has_client_accepted_protection_needs: Boolean
  client_declined_protection_needs_reason: String
  client_advice_limit_details: String
  has_existing_policies: Boolean
  death_needs_income_amount: String
  death_needs_income_term: Int
  death_needs_lump_sum_amount: String
  death_needs_lump_sum_term: Int
  has_employee_benefits: Boolean
  illness_needs_income_amount: String
  illness_needs_income_term: Int
  death_needs_lump_sum_term_justification: String
  death_needs_income_term_justification: String
  illness_needs_deferrment_period_weeks: Int
  illness_needs_term_justification: String
  trust_details: String
  length_of_time_payment_without_work: LengthOfTimePaymentWithoutWork
  illness_needs_met_by_preexisting: Boolean
  death_lump_sum_needs_met_by_preexisting: Boolean
  death_income_needs_met_by_preexisting: Boolean
  any_remaning_shortfalls: Boolean
  justify_remaining_shortfalls: String
  additional_notes: String
  include_employee_benefits_in_shortfall: Boolean
  employee_benefits_shortfall_reasoning: String
  client_protection_objectives_circumstances: String
  insistent_client_indicator: Boolean
  inheritance_advice_indicator: Boolean
  income_has_critical_illness_requirement: Boolean
  income_has_death_requirement: Boolean
  lump_sum_has_critical_illness_requirement: Boolean
  lump_sum_has_death_requirement: Boolean
  death_needs_lump_sum_client_ids: [String]
  death_needs_income_client_ids: [String]
  illness_needs_client_ids: [String]
  funeral_needs_client_ids: [String]
  funeral_needs_lump_sum_amount: String
  funeral_needs_justification: String
  funeral_needs_met_by_preexisting: Boolean
  recommend_placing_in_trust: Boolean
  specific_trust_recommendation: Boolean
  client_confirms_terms_exceeding_retirement_affordable: Boolean
  product_details: String
  client_advice_limit: Boolean
  suitability_report_recommendation_introduction_protection: String
  additional_considerations_detail: String

  rebroke_decision_advantages_and_disadvantages: String
  additional_budget_details: String
  death_needs_lump_sum_amount_justification: String
  death_needs_income_amount_justification: String
  illness_needs_income_amount_justification: String
  requirements: ProtectionRequirementsInput
}

#MI case List
enum GetMiCaseListOrderBy {
  CASE_TYPE
  CREATED_AT
  MIN_NOTE_DEADLINE
  MODIFIED_AT
  STATUS
  TRANSITION_AT
}

enum GetMiCaseListOrder {
  ASC
  DESC
}

type MICase {
  address: LutherAddress
  advisor_id: ID
  advisor_name: String
  case_id: ID!
  case_status: CaseStatus
  case_type: MortgageReason
  last_editor_type: String
  client_ids: [String!]
  clients: [ClientVersion]
  introducerClients: [Client]
  created_at: String
  introducer_id: ID
  introducer: Organisation
  introducer_user: User
  related_cases: [RelatedCase]
  related_users: [RelatedUser]
  lender_id: ID
  lender: Organisation
  modified_at: String
  mortgage_amount: BigInt
  org_id: ID
  org_ids: [ID]
  notes: [CaseNotes]
  transition_at: String
  submitted_date: String
  review_date: String
  completed_date: String
  recommendation_date: String
  first_submitted_date: String
  first_review_date: String
  first_completed_date: String
  first_recommendation_date: String
  regulated_by_name: String
  offer_date: String
  exchange_date: String
  first_offer_date: String
  first_exchange_date: String
  logged_in_to_client_portal: Boolean
  recent_client_doc: Boolean
  credit_report_done: Boolean
  meeting_booked: Boolean
  organisation: Organisation
  organisation_name: String
  predicted_revenue: BigInt
  property_id: ID
  offer_expiry_date: String
  flags: [CaseDetailsFlagFlagType]
}

type CaseReferral {
  address: LutherAddress
  advisor_id: ID
  advisor_name: String
  advisor: User
  case_id: ID!
  case_status: CaseStatus
  case_type: MortgageReason
  last_editor_type: String
  client_ids: [String!]
  clients: [ClientVersion]
  introducerClients: [Client]
  created_at: String
  introducer_id: ID
  introducer: Organisation
  related_users: [RelatedUser]
  lender_id: ID
  lender: Organisation
  modified_at: String
  mortgage_amount: BigInt
  org_id: ID
  org_ids: [ID]
  notes: [CaseNotes]
  transition_at: String
  submitted_date: String
  review_date: String
  completed_date: String
  recommendation_date: String
  first_submitted_date: String
  first_review_date: String
  first_completed_date: String
  first_recommendation_date: String
  regulated_by_name: String
  offer_date: String
  exchange_date: String
  first_offer_date: String
  first_exchange_date: String
  logged_in_to_client_portal: Boolean
  recent_client_doc: Boolean
  credit_report_done: Boolean
  meeting_booked: Boolean
  organisation: Organisation
  organisation_name: String
  predicted_revenue: BigInt
  property_id: ID
  offer_expiry_date: String
  flags: [CaseDetailsFlagFlagType]
}

type CaseNotes {
  assignee_id: String
  communication_type: CommunicationType
  deadline: String
  primary_type: NoteType
  priority: Int
  status: NoteStatus
  title: String
  template_id: String
}

input GetReferredCaseListFilterInput {
  bookmark: Int
  case_status: [CaseStatus!]
  case_types: [MortgageReason!]
  end_date: String
  lender_ids: [String!]
  order: GetMiCaseListOrder
  order_by: GetMiCaseListOrderBy
  page_size: Int
  start_date: String
  introducer_ids: [String!]
  introducer_org_ids: [String!]
}

input GetMiCaseListFilterInput {
  advisor_ids: [String!]
  bookmark: Int
  case_status: [CaseStatus!]
  case_types: [MortgageReason!]
  end_date: String
  introducer_ids: [String!]
  introducer_org_ids: [String!]
  introducer_org_names: [String!]
  lender_ids: [String!]
  order: GetMiCaseListOrder
  order_by: GetMiCaseListOrderBy
  page_size: Int
  start_date: String
  reminder_max_deadline: String
  reminder_min_deadline: String
  no_reminders_outstanding: Boolean
  relationship_type: String
  related_user_ids: [String!]
  date_filter_from: String
  date_filter_to: String
  date_filter_type: String
  regulated_by: String
  case_flags: [CaseDetailsFlagFlagType!]
  org_ids: [String!]
  tags: [String!]
  cases_with_amended_fees: Boolean
  cases_with_fees_added_to_loan: Boolean
  product_transfer: Boolean
  vulnerable: Boolean
  product_coverage_type: String
  product_coverage_advice: String
  scope: String
}

input GetCaseReferralListFilterInput {
  advisor_ids: [String!]
  bookmark: Int
  case_status: [CaseStatus!]
  case_types: [MortgageReason!]
  end_date: String
  introducer_ids: [String!]
  introducer_org_ids: [String!]
  introducer_org_names: [String!]
  lender_ids: [String!]
  order: GetMiCaseListOrder
  order_by: GetMiCaseListOrderBy
  page_size: Int
  start_date: String
  reminder_max_deadline: String
  reminder_min_deadline: String
  no_reminders_outstanding: Boolean
  relationship_type: String
  related_user_ids: [String!]
  date_filter_from: String
  date_filter_to: String
  date_filter_type: String
  regulated_by: String
  case_flags: [CaseDetailsFlagFlagType!]
  org_ids: [String!]
  tags: [String!]
  cases_with_amended_fees: Boolean
  cases_with_fees_added_to_loan: Boolean
  product_transfer: Boolean
  vulnerable: Boolean
  product_coverage_type: String
  product_coverage_advice: String
}

input DateFilterTypeInput {
  created_at: String
  modified_at: String
  transition_at: String
  completed_date: String
  review_date: String
  recommendation_date: String
  submitted_date: String
  offer_date: String
  exchange_date: String
  first_completed_date: String
  first_review_date: String
  first_recommendation_date: String
  first_submitted_date: String
  first_offer_date: String
  first_exchange_date: String
}

type GetMiCaseListFilter {
  advisor_ids: [String!]
  bookmark: Int
  case_status: [CaseStatus!]
  case_types: [MortgageReason!]
  end_date: String
  introducer_ids: [String!]
  lender_ids: [String!]
  related_user_ids: [String!]
  relationship_type: String
  order: GetMiCaseListOrder
  order_by: GetMiCaseListOrderBy
  page_size: Int
  start_date: String
  case_flags: [CaseDetailsFlagFlagType!]
  org_ids: [String!]
}

type GetCaseReferralListFilter {
  advisor_ids: [String!]
  bookmark: Int
  case_status: [CaseStatus!]
  case_types: [MortgageReason!]
  end_date: String
  introducer_ids: [String!]
  lender_ids: [String!]
  related_user_ids: [String!]
  relationship_type: String
  order: GetMiCaseListOrder
  order_by: GetMiCaseListOrderBy
  page_size: Int
  start_date: String
  case_flags: [CaseDetailsFlagFlagType!]
  org_ids: [String!]
}

type GetMiCaseListStats {
  predicted_revenue: BigInt!
  count: Int!
}

type GetCaseReferralListStats {
  predicted_revenue: BigInt!
  count: Int!
}

type GetMiCaseListResponse {
  cases: [MICase!]!
  filters: GetMiCaseListFilter
  next_page: GetMiCaseListFilter
  stats: GetMiCaseListStats!
}

type GetCaseReferralListResponse {
  cases: [CaseReferral!]!
  filters: GetCaseReferralListFilter
  next_page: GetCaseReferralListFilter
  stats: GetCaseReferralListStats!
}

enum ReferralType {
  INVALID_TYPE
  CONVEYANCING
  GENERAL_INSURANCE
  SURVEYING
  WILLS_AND_PROBATE
}

enum ReferralPartnerType {
  INVALID_REFERRAL_PARTNER
  SIMPLIFY_MHM
  UINSURE
  SBVS
  SDL
  DECLINED_REFERRAL
  HONEY_LEGAL
  SMOOVE
  FP_SURVEYING
  HIGOS
  TOWERGATE
  BENNETT_ALEXANDER
  GALLAGHER
}

enum ReferralStatus {
  INVALID_STATUS
  REFERRED
  ACCEPTED
  DECLINED_BY_CUSTOMER
  DECLINED_BY_PROVIDER
}

enum ReferralSource {
  UNSPECIFIED
  CLIENT
  USER
  AUTO_REFER
}

enum ReferralReason {
  INVALID_REFERRAL_REASON
  REFERRAL_REASON_RETIREMENT_LENDING
  REFERRAL_REASON_ADVERSE_CREDIT
  REFERRAL_REASON_COMPLEX_INCOME
  REFERRAL_REASON_SECOND_CHARGE
  REFERRAL_REASON_BRIDGING
  REFERRAL_REASON_COMMERCIAL_MORTGAGE
  REFERRAL_REASON_INCOME_PROTECTION
  REFERRAL_REASON_LIFE_OR_CRITICAL_ILLNESS
  REFERRAL_REASON_PRIVATE_MEDICAL_INSURANCE
  REFERRAL_REASON_HOME_INSURANCE
  REFERRAL_REASON_OTHER
}

input ReferralCaseInput {
  case_id: ID!
  client_ids: [String!]
  preference_mortgage_reason: MortgageReason!
  target_user_id: String
  target_organisation_id: String
  fee_option_title: String
  referral_source: ReferralSource
  referral_source_id: String
  referral_reason: ReferralReason
}

type ConveyancingUpdate {
  conveyancing_update_id: ID!
  source: ConveyancingUpdateSource
  type: ConveyancingUpdateType
  created_at: String!
  date: String
  property: ConveyancingUpdateProperty
}

enum ConveyancingUpdateSource {
  INVALID_CONVEYANCING_UPDATE_SOURCE
  SIMPLIFY_MHM
  LMS
  CLIENT
  USER
  SMOOVE
}

enum ConveyancingUpdateType {
  INVALID_CONVEYANCING_UPDATE_MESSAGE_TYPE
  StarterPackInstructionsReceived
  CompletionMoniesRequested
  CompletionSetForExpected
  ContractClientReceivedActual
  ContractClientReceivedExpected
  ContractSolicitorReceivedActual
  ContractSolicitorReceivedExpected
  ContractSolicitorSent
  DateAborted
  DateCompleted
  DateExchanged
  DateInstructed
  DateOutOfAbeyance
  DateReactivated
  DateReadyToExchangeEnd
  DateReadyToExchangeStart
  DateSalesMemorandumReceived
  DateSentToAbeyance
  DateStarterPackReceived
  DateSuspended
  EnquiriesLastAnswered
  EnquiriesLastRaised
  EnquiriesRepliesApproved
  ExchangeCompleteTransferReceived
  MortgageDeedToBorrower
  MortgageOfferReceived
  MortgageReceivedClient
  MortgageReportClient
  MortgageSignedReceived
  RedemptionReceived
  RedemptionRequested
  SearchesReceivedActual
  SearchesReportedClient
  SearchesRequested
  StarterPackIdSatisfiedDate
  StarterPackInitialPayment
  StarterPackSent
  StatusInfoReceivedBorrower
  TitleChecked
  TitleContractApproved
  TitleDeedsReceived
  TitleDeedsRequested
  TitleDocumentsClientActual
  TitleDocumentsClientExpected
  TitleDocumentsReceivedActual
  TitleDocumentsReceivedExpected
  TitleDocumentsRequested
  TitleInformationRequested
  TitleReceivedActual
  TitleReceivedExpected
  TitleReportClient
  TitleRequisitionsEnquiries
  ScotlandCoalReportReceived
  ScotlandCustomerAcceptance
  ScotlandCustomerIDVerificationReceived
  ScotlandDeedsSentToPurchasersSolicitor
  ScotlandDepositReceived
  ScotlandDispositionSignedByCustomer
  ScotlandDateOfEntry
  ScotlandLoanFundsReceived
  ScotlandMissivesConcluded
  ScotlandObservationsReceivedFromPurchasersSolicitor
  ScotlandVerballyAccepted
  ScotlandPersonalSearchReceived
  ScotlandQualifiedAcceptanceReceived
  ScotlandStandardSecuritySigned
}

enum ConveyancingUpdateProperty {
  INVALID_CONVEYANCING_UPDATE_PROPERTY
  PURCHASE_OR_REMORTGAGE_PROPERTY
  SALE_PROPERTY
}

input CaseAssigneesInput {
  cases: [CaseAssigneesItemInput!]
}

input CaseAssigneesItemInput {
  case_id: ID!
  owner_user_id: ID
  related_users: [RelatedUserInput]
}

type CaseAssignees {
  cases: [CaseAssigneesItem]
}

type CaseAssigneesItem {
  case_id: ID!
  owner_user_id: ID
  related_users: [RelatedUser]
}

input AddManualFlagToCaseInput {
  case_id: ID
  flag_type: CaseDetailsFlagFlagType
}

type AddManualFlagToCase {
  case_id: ID
  flag_type: CaseDetailsFlagFlagType
}

input BulkCreateRemoLeadsInput {
  cases: [CaseDetailsRemoLeadsInput]
}

input CaseDetailsRemoLeadsInput {
  case_id: ID!
  auto_assign: Boolean
}

type BulkCreateRemoLeads {
  linked_lead_cases: [CaseDetailsRemoLeads!]!
}

type CaseDetailsRemoLeads {
  complete_case_id: ID!
  lead_case_id: ID!
}

type Editor {
  id: ID!
  type: EditorType
}

type TimelineVersionObject {
  id: ID!
  version_from: Int
  version_to: Int
  show_history: Int
}

type CaseVersionTimelineSummary {
  editor: Editor
  editor_name: String
  case_id: ID!
  status: CaseStatus!
  version_from: Int!
  version_to: Int!
  show_history: Int
  transitioned_at: String!
  property_versions: [TimelineVersionObject!]
  client_versions: [TimelineVersionObject!]
  mortgage_versions: [TimelineVersionObject!]
}

type CaseVersionsTimelineResponse {
  timeline: [CaseVersionTimelineSummary!]
}

input AddendumReasonsInput {
  cover_change_self_identified: String
  cover_change_system_identified: String
  additional_details: String
}

type AddendumReasons {
  cover_change_self_identified: String
  cover_change_system_identified: String
  additional_details: String
}
